/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1706227200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Jan 26 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "airtable",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Airtable",
          "depth": 0,
          "id": "connect-to-airtable"
        }, {
          "value": "Create Airtable personal access token",
          "depth": 1,
          "id": "create-airtable-personal-access-token"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      p: "p",
      a: "a",
      strong: "strong",
      h3: "h3",
      img: "img",
      ul: "ul",
      li: "li",
      code: "code",
      h4: "h4"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Objects"
            }), _jsx(_components.td, {
              children: "Insert new objects without updating others or push new objects and update fields that change"
            }), _jsx(_components.td, {
              children: "Insert, Upsert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Object fields"
            }), _jsx(_components.td, {
              children: "Update particular fields on existing objects without adding new objects"
            }), _jsx(_components.td, {
              children: "Update"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-airtable",
        children: "Connect to Airtable"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Airtable"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to Airtable using an Airtable personal access token."]
      }), "\n", _jsx(_components.h3, {
        id: "create-airtable-personal-access-token",
        children: "Create Airtable personal access token"
      }), "\n", _jsxs(_components.p, {
        children: ["Navigate to your ", _jsx(_components.a, {
          href: "https://airtable.com/create/tokens",
          children: "Airtable Personal access tokens page"
        }), " and click ", _jsx(_components.strong, {
          children: "Create token"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-airtable-pat.png",
          alt: "Airtable account overview page"
        })
      }), "\n", _jsx(_components.p, {
        children: "Give your token a descriptive name, for example, \"Hightouch Integration Key,\" and grant your token the following scopes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "data.records:read"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "data.records:write"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "schema.bases:read"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "schema.bases:write"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-airtable-scopes.png",
          alt: "Airtable account overview page"
        })
      }), "\n", _jsx(_components.p, {
        children: "Grant your token access to the bases and workspaces you need Hightouch to access, and then copy your token to use it in Hightouch."
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Airtable destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Airtable destination you want to sync to."]
      }), "\n", _jsxs(_components.p, {
        children: ["Choose the ", _jsx(_components.strong, {
          children: "Airtable base"
        }), " and ", _jsx(_components.strong, {
          children: "table"
        }), " you want to sync to using the two drop-down lists."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-airtable-configurations.png",
          alt: "Airtable configuration in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Then select the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes",
          children: "mode"
        }), " for how to alter Airtable objects:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), ": pushes new objects to Airtable. It doesn't update any objects."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert"
          }), ": pushes new objects to Airtable and updates fields that change in your warehouse."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Update"
          }), ": updates particular fields on existing objects in Airtable. It doesn't add new objects."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["If you are using ", _jsx(_components.strong, {
          children: "Upsert"
        }), " or ", _jsx(_components.strong, {
          children: "Update"
        }), " mode, you must select a record matching field.\nYou can match records from your source to your Airtable workspace by the table's primary key or any other unique fields in the table.\nYou can choose the field you would like to use from the dropdown."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-airtable-record-matching.png",
          alt: "Airtable record matching in the Hightouch UI"
        })
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["You should match the record using a unique identifier with a ", _jsx(_components.code, {
            children: "string"
          }), " type, otherwise the sync may not update the intended records properly."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to any of your Airtable properties. The Airtable API performs best-effort\nautomatic data conversion from string values. You can choose the field names from the drop-down lists."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-airtable-field-mapping.png",
          alt: "Airtable field mapping in the Hightouch UI"
        })
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["When syncing data to ", _jsx(_components.a, {
            href: "https://support.airtable.com/docs/linking-records-in-airtable",
            children: "linked"
          }), " or ", _jsx(_components.a, {
            href: "https://support.airtable.com/docs/lookup-field-overview",
            children: "lookup"
          }), " Airtable fields, make sure to set your model column to ", _jsx(_components.a, {
            href: "/models/data-types-casting#data-types",
            children: _jsx(_components.code, {
              children: "string"
            })
          }), " type. Otherwise, the values synced to these fields won't appear in Airtable."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "duplicated-records",
        children: "Duplicated records"
      }), "\n", _jsxs(_components.p, {
        children: ["Duplicated records in Airtable may occur if a data type mismatch between the ", _jsx(_components.a, {
          href: "/syncs/record-matching#model-column-selection",
          children: "model column"
        }), " and the Airtable field selected for ", _jsx(_components.a, {
          href: "#record-matching",
          children: "record matching"
        }), " occurs."]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch ", _jsx(_components.a, {
          href: "/models/data-types-casting#view-model-data-types",
          children: "automatically stringifies"
        }), " the model column you select for your ", _jsx(_components.a, {
          href: "/getting-started/concepts#unique-primary-key-requirement",
          children: "primary key"
        }), ". If you choose your model's primary key for record matching, you need to select a ", _jsx(_components.code, {
          children: "string"
        }), " type Airtable field for record matching as well. If you select a ", _jsx(_components.code, {
          children: "number"
        }), " type Airtable field, duplicate records could occur because, for example, ", _jsx(_components.code, {
          children: "\"1234\""
        }), " doesn't match ", _jsx(_components.code, {
          children: "1234"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you want to use the primary key column for record matching, but need to match it to a ", _jsx(_components.code, {
          children: "number"
        }), " type Airtable field, you can use ", _jsx(_components.a, {
          href: "/models/data-types-casting#sql-aliasing",
          children: "SQL aliasing"
        }), " to create a new column. The new column is a \"copy\" of the primary key column, but because Hightouch won't automatically stringify it, you can cast it to ", _jsxs(_components.a, {
          href: "/models/data-types-casting#data-types",
          children: [_jsx(_components.code, {
            children: "number"
          }), " type"]
        }), " and use it for record matching."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
